import PropTypes from 'prop-types'
import { useState, useEffect, useContext } from 'react'
import Modal from 'react-bootstrap/Modal'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { formatearFecha, validarRangoFechas } from '../../helpers/dateValidation'
import { toastErrorLog } from '../../helpers/toastErrorLog'
import { CarritoContext } from '../context/carritoContext'
import Precios from '../reutilizables/precios/Precios'
import { adjuntoBuilder } from '../reutilizables/upload/adjuntoBuilder'
import { Upload } from '../reutilizables/upload/Upload'
import * as archivoAdjuntoService from '../servicios/archivoAdjuntoService'
import * as mensajesService from '../servicios/mensajesService'
import * as presupuestosItemService from '../servicios/presupuestosItemService'
import * as presupuestosService from '../servicios/presupuestosService'
import * as publicacionesService from '../servicios/publicacionesService'
import Input from '../ui/Input'
import styles from './PresupuestoForm.module.css'
import PresupuestoFormItem from './PresupuestoFormItem'
import PresupuestoFormItemList from './PresupuestoFormItemList'

const ID_ADJUNTOS_PRESUPUESTOS = 9
const MERCADO_PAGO = 7

const PresupuestoForm = ({
  idPresupuesto,
  presupuesto: presupuestoProp,
  usuarioId,
  handleEstado,
  canAdministrate,
  esVendedor,
  onGuardar
}) => {
  const context = useContext(CarritoContext)
  const [presupuesto, setPresupuesto] = useState({
    idPresupuesto: presupuestoProp.idPresupuesto,
    descripcion: presupuestoProp.descripcion,
    fechaEntrega: presupuestoProp.fechaEntrega,
    fechaValidez: presupuestoProp.fechaValidez,
    idEstado: presupuestoProp.idEstado
  })

  const [mediosPago, setMediosPago] = useState([])
  const [presupuestoItems, setPresupuestoItems] = useState([])
  const [confirmado, setConfirmado] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [nombreAdjunto, setNombreAdjunto] = useState('')

  const { fechaEntrega, fechaValidez, descripcion } = presupuesto
  const fechaHoy = new Date()

  const validarFechasPresupuesto = (fechaEntrega, fechaValidez) => {
    const hoy = new Date()

    if (!fechaEntrega || !fechaValidez) {
      return 'Las fechas son obligatorias.'
    }

    if (validarRangoFechas(fechaEntrega, hoy, 'day')) {
      return 'La fecha de entrega no puede ser una fecha pasada.'
    }

    if (validarRangoFechas(fechaValidez, hoy, 'day')) {
      return 'La fecha de validez no puede ser una fecha pasada.'
    }

    if (validarRangoFechas(fechaValidez, fechaEntrega, 'day')) {
      return 'La fecha de validez no puede ser anterior a la fecha de entrega.'
    }

    return null
  }

  const totalNeto = presupuestoItems.reduce((acc, item) => acc + Number(item.total), 0)
  const totalIva = presupuestoItems.reduce(
    (acc, item) => acc + Number(item.total) * ((Number(item.alicuota) || 0) / 100),
    0
  )
  const hayCambios =
    presupuestoProp.idPresupuesto &&
    (descripcion !== presupuestoProp.descripcion ||
      fechaEntrega !== presupuestoProp.fechaEntrega ||
      fechaValidez !== presupuestoProp.fechaValidez)

  const fetchAdjunto = async () => {
    const { data } = await archivoAdjuntoService.buscarUltimoAdjunto(
      presupuestoProp.idVendedor || usuarioId,
      ID_ADJUNTOS_PRESUPUESTOS,
      idPresupuesto
    )
    setNombreAdjunto(data?.nombre || '')
  }

  const abrirAdjunto = async () => {
    try {
      const { data } = await archivoAdjuntoService.buscarArchivo(
        presupuestoProp.idVendedor || usuarioId,
        ID_ADJUNTOS_PRESUPUESTOS,
        idPresupuesto
      )
      if (data?.adjunto?.archivo) {
        const { dataAdjunto, adjunto } = data
        const blob = Uint8Array.from(atob(dataAdjunto), (c) => c.charCodeAt(0))

        await adjuntoBuilder(blob, adjunto, adjunto?.archivo || 'adjunto')
      } else {
        toast.warn('No se encontraron archivos adjuntos.')
      }
    } catch (error) {
      const mensaje = `Ocurrió un error al obtener el adjunto.`
      toastErrorLog(error, mensaje, 'presupuestos')
    }
  }

  useEffect(() => {
    presupuestoProp.idPresupuesto && fetchAdjunto()
  }, [presupuestoProp.idPresupuesto, nombreAdjunto])

  useEffect(() => {
    if (presupuestoProp.idPresupuesto) {
      setPresupuesto(presupuestoProp)
      obtenerItems(idPresupuesto)
      obtenerMediosPago(presupuestoProp.idPublicacion, idPresupuesto)
    }
  }, [presupuestoProp])

  const obtenerMediosPago = async (idPublicacion, idPresupuesto) => {
    const { data: mediosPago } = await publicacionesService.BuscarMedioPagoPublicacion(
      idPublicacion,
      idPresupuesto
    )
    setMediosPago(mediosPago)
  }

  const obtenerItems = async (idPresupuesto) => {
    const { data: presupuestoItemTemp } =
      await presupuestosItemService.buscarItemPorId(idPresupuesto)
    setPresupuestoItems(presupuestoItemTemp)
  }

  const onSubmit = async (e) => {
    e.preventDefault()

    const error = validarFechasPresupuesto(fechaEntrega, fechaValidez)
    const tieneMedioPago = mediosPago.some((m) => m.tienePresupuesto)
    if (!tieneMedioPago) {
      toast.error('Debes seleccionar al menos un medio de pago antes de confirmar el presupuesto.')
      return
    }
    if (error) {
      toast.error(error)
      return
    }

    setShowModal(true)
    setConfirmado(true)
  }

  const updatePresupuestoItems = async (presupuestoItem) => {
    const { data: presupuestoItemsTemp } = await presupuestosItemService.buscarItemPorId(
      presupuestoItem.idPresupuesto
    )
    setPresupuestoItems(presupuestoItemsTemp)
  }

  const deleteItemList = async (presupuestoItem) => {
    try {
      const newItems = presupuestoItems.filter((item) => item.idPresupuestoItem !== presupuestoItem)
      setPresupuestoItems(newItems)

      const deleteItem = {
        idPresupuesto: presupuestoProp.idPresupuesto,
        idPresupuestoItem: presupuestoItem
      }
      await presupuestosItemService.eliminar(deleteItem)
    } catch (error) {
      const mensaje = `Ocurrió un error al eliminar el item.`
      toastErrorLog(error, mensaje, 'presupuestos')
    }
  }

  const handleClose = () => {
    setShowModal(false)
  }

  const onChange = (e) => {
    setPresupuesto({ ...presupuesto, [e.target.name]: e.target.value })
  }

  const onSave = async () => {
    const error = validarFechasPresupuesto(fechaEntrega, fechaValidez)

    if (error) {
      toast.error(error)
      return
    }
    const presupuestoTemp = {
      idPresupuesto: presupuestoProp.idPresupuesto,
      descripcion: descripcion,
      fechaEntrega: fechaEntrega,
      fechaValidez: fechaValidez,
      idEstado: presupuesto.confirmar ? 1 : 0
    }
    await presupuestosService.actualizar(presupuestoTemp)
    await onGuardar()
    toast.success('La información fué actualizada.')
  }

  const onMedioChange = async (e) => {
    const idMedio = parseInt(e.target.value)
    const checked = e.target.checked
    setMediosPago((prev) =>
      prev.map((medio) =>
        medio.idMedioPago === idMedio ? { ...medio, tienePresupuesto: checked } : medio
      )
    )
    const presupuestoTemp = {
      idPresupuesto: presupuestoProp.idPresupuesto,
      descripcion: presupuestoProp.descripcion,
      fechaEntrega: presupuestoProp.fechaEntrega,
      fechaValidez: presupuestoProp.fechaValidez,
      idEstado: presupuesto.confirmar ? 1 : 0,
      idMedioPago: parseInt(e.target.value)
    }

    await presupuestosService.actualizar(presupuestoTemp)
  }

  const enviarComprador = async () => {
    try {
      setConfirmado(!confirmado)
      setShowModal(false)
      handleEstado('Confirmado')

      const presupuestoTemp = {
        idPresupuesto: presupuestoProp.idPresupuesto,
        descripcion: descripcion,
        fechaEntrega: fechaEntrega,
        fechaValidez: fechaValidez,
        idEstado: confirmado ? 1 : 0
      }
      const mensaje = {
        idMensajePadre: null,
        idPublicacion: null,
        idPedidoItem: null,
        idPresupuesto: presupuestoProp.idPresupuesto,
        idUsuarioChat: presupuestoProp.idUsuario,
        idComprador: presupuestoProp.idUsuario,
        idVendedor: usuarioId,
        texto: '',
        privado: false,
        idMensajeTipo: 7
      }
      await mensajesService.insertar(mensaje)
      await presupuestosService.actualizar(presupuestoTemp)
      setPresupuesto((prev) => ({ ...prev, idEstado: presupuestoTemp.idEstado }))
    } catch (error) {
      const mensaje = `Ocurrió un error al enviar presupuesto.`
      toastErrorLog(error, mensaje, 'general')
    }
  }

  const setIdPresupuestoSel = (idPre) => {
    context.setIdPublicacionComprar(0)
    context.setIdPresupuestoComprar(idPre)
  }

  return (
    <>
      <form onSubmit={onSubmit}>
        <div className="row">
          <div className="row">
            <div className="cont-descripcion col">
              <p className="titulo-descripcion-detalle">Descripción:</p>
              <textarea
                name="descripcion"
                className="text-area"
                cols="35"
                rows="6"
                placeholder="Escribe aquí­ la descripción de tu presupuesto."
                value={descripcion}
                onChange={onChange}
                required
                disabled={!canAdministrate}
              ></textarea>
            </div>
          </div>
        </div>
        <div className="row mt-2 ">
          <div className="col-12 col-lg-5">
            <div className="cont-descripcion">
              <p className="titulo-descripcion-detalle">Medios de Pago:</p>
              {mediosPago
                .filter((medioPago) => canAdministrate || medioPago.tienePresupuesto)
                .map((medioPago) => (
                  <div key={medioPago.idMedioPago} className="d-flex align-items-center mb-2 ">
                    {esVendedor && (
                      <input
                        type="checkbox"
                        name={'mediopago'}
                        value={medioPago.idMedioPago}
                        onChange={onMedioChange}
                        defaultChecked={medioPago.tienePresupuesto ? 'checked' : ''}
                        disabled={!canAdministrate}
                      />
                    )}
                    <div>
                      <i
                        className={medioPago.icono}
                        style={{ marginRight: '5px', marginLeft: '5px' }}
                        aria-hidden="true"
                      />
                      {medioPago.nombre}
                      {medioPago.idMedioPago === MERCADO_PAGO && medioPago.descripcion && (
                        <small className="warningMessage">{`(${medioPago.descripcion})`}</small>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
          <div className="col-12 col-lg-2">
            <div className="cont-descripcion">
              <p className="titulo-descripcion-detalle">Fecha Entrega:</p>
              <Input
                name="fechaEntrega"
                type="date"
                value={fechaEntrega}
                onChange={onChange}
                min={formatearFecha(fechaHoy, 'YYYY-MM-DD')}
                required
                disabled={!canAdministrate}
                className="input-text-bordered"
              />
            </div>
          </div>
          <div className="col-12 col-lg-2">
            <div className="cont-descripcion">
              <p className="titulo-descripcion-detalle">Validez Presupuesto:</p>
              <Input
                name="fechaValidez"
                type="date"
                value={fechaValidez}
                min={formatearFecha(fechaHoy, 'YYYY-MM-DD')}
                onChange={onChange}
                required
                disabled={!canAdministrate}
                className="input-text-bordered"
              />
            </div>
          </div>
          <div className="col-12 col-lg-3">
            <div className="cont-descripcion">
              <p className="titulo-descripcion-detalle">Adjunto: </p>
              {canAdministrate && (
                <Upload
                  idDoc={presupuestoProp.idPresupuesto}
                  idPerfil={usuarioId}
                  idTipo={'9'}
                  onCarga={() => setNombreAdjunto(null)}
                />
              )}
              {nombreAdjunto && (
                <span className={styles.archivo} onClick={abrirAdjunto} title="Abrir adjunto">
                  {nombreAdjunto}
                </span>
              )}
            </div>
          </div>
        </div>
        {canAdministrate && presupuesto.idEstado === 0 && presupuestoItems.length > 0 && (
          <div className="row mt-2 ">
            <div className="col-12 d-flex justify-content-center">
              <button type="submit" className="btn btn-preguntar px-3 w-auto">
                Confirmar Presupuesto
              </button>
            </div>
          </div>
        )}

        {canAdministrate && (
          <Modal show={showModal} onHide={handleClose} size={'lg'} animation={false}>
            <Modal.Header closeButton>
              <Modal.Title>
                <h3>Confirmar Presupuesto</h3>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>
                Estás por confirmar tu presupuesto. Esta acción no se podrá deshacer y le avisaremos
                a tu comprador que el presupuesto ya está disponible para su pago.
              </p>
              ¿Estás seguro?
            </Modal.Body>
            <Modal.Footer>
              <button type="button" className="btn btn-enviar" onClick={handleClose}>
                Cancelar
              </button>
              <button type="button" className="btn btn-volver-comprar" onClick={enviarComprador}>
                Confirmar Presupuesto
              </button>
            </Modal.Footer>
          </Modal>
        )}
      </form>
      {presupuestoItems.length > 0 && (
        <div>
          <p className="titulo-descripcion-detalle">
            <b>Detalle Presupuesto:</b>
          </p>
          <div className="row">
            <div className="col">
              <b>Item</b>
            </div>
            <div className="col text-right">
              <b>Cantidad</b>
            </div>
            <div className="col text-right">
              <b>Precio</b>
            </div>
            <div className="col">
              <b>Unidad Medida</b>
            </div>
            <div className="col text-right">
              <b>Total</b>
            </div>
            <div className="col text-right">
              <b>% IVA</b>
            </div>
            <div className="col text-right"></div>
          </div>
          <hr />
        </div>
      )}

      {presupuestoItems.map((item) => (
        <PresupuestoFormItemList
          key={item.idPresupuestoItem}
          item={item}
          idEstado={presupuestoProp.idEstado}
          onDelete={deleteItemList}
          canAdministrate={canAdministrate}
        />
      ))}
      {presupuestoItems.length > 0 && (
        <div className="row mt-2">
          <div>
            <Precios
              className={styles.precios}
              nombre="Subtotal sin IVA"
              precio={totalNeto}
              monedaSimbolo="$"
            />
            <Precios className={styles.precios} nombre="IVA" precio={totalIva} monedaSimbolo="$" />
            <Precios
              className={styles.precios}
              nombre="Total a pagar"
              precio={totalNeto + totalIva}
              monedaSimbolo="$"
            />
          </div>
        </div>
      )}
      {!canAdministrate &&
        presupuesto.idEstado === 1 &&
        validarFechasPresupuesto(fechaEntrega, fechaValidez) !== null && (
          <p className="text-danger text-center mt-3">
            Este presupuesto venció el {formatearFecha(fechaValidez)}. Contactá al vendedor para que
            emita uno nuevo.
          </p>
        )}
      {!esVendedor &&
        presupuesto.idEstado === 1 &&
        validarFechasPresupuesto(fechaEntrega, fechaValidez) === null && (
          <Link
            to={'/Compras'}
            className={styles.comprarAhoraBtn}
            onClick={() => setIdPresupuestoSel(presupuestoProp.idPresupuesto)}
          >
            Comprar Ahora
          </Link>
        )}
      {canAdministrate && presupuesto.idEstado === 0 ? (
        <>
          <PresupuestoFormItem
            idPresupuesto={presupuestoProp.idPresupuesto}
            presupuestoData={updatePresupuestoItems}
          />
          <div className="row mt-2">
            <div className="col-4"></div>
            {hayCambios && (
              <div className="col-4">
                <button type="button" className="btn btn-preguntar" onClick={onSave}>
                  Guardar Presupuesto
                </button>
              </div>
            )}
            <div className="col-4"></div>
          </div>
        </>
      ) : null}
    </>
  )
}

PresupuestoForm.propTypes = {
  idPresupuesto: PropTypes.number.isRequired,
  presupuesto: PropTypes.shape({
    idPresupuesto: PropTypes.number.isRequired,
    descripcion: PropTypes.string.isRequired,
    fechaEntrega: PropTypes.string.isRequired,
    fechaValidez: PropTypes.string.isRequired,
    idEstado: PropTypes.number.isRequired,
    idPublicacion: PropTypes.number,
    idVendedor: PropTypes.number,
    idUsuario: PropTypes.number
  }).isRequired,
  usuarioId: PropTypes.number.isRequired,
  handleEstado: PropTypes.func.isRequired,
  canAdministrate: PropTypes.bool.isRequired,
  esVendedor: PropTypes.bool,
  onGuardar: PropTypes.func
}

export default PresupuestoForm
